<template>
  <v-main>
    <v-container fluid>
      <v-card elevation="3" rounded="xxl">
        <v-card-title class="mt-4">
          <v-btn
            class="mx-2"
            fab
            x-small
            color="primary"
            @click="$router.back()"
          >
            <v-icon dark> mdi-arrow-left </v-icon>
          </v-btn>
          {{ isEditMode ? "Edit" : "Add" }} {{ typeLabel }} Levels
          <v-chip
            v-if="form.type !== ''"
            :color="form.type == '0' ? 'amber darken-2' : 'deep-purple'"
            text-color="white"
            small
            class="ml-3"
          >
            <v-icon left size="14">{{ form.type == '0' ? 'mdi-currency-usd' : 'mdi-star' }}</v-icon>
            {{ typeLabel }}
          </v-chip>
        </v-card-title>
        <v-card-text>
          <!-- Level Forms -->
          <div
            v-for="(level, index) in levels"
            :key="index"
            :class="[
              'level-form',
              { 'level-form-new': isEditMode && !level.id && isLevelComplete(level) },
              { 'level-form-incomplete': isEditMode && !level.id && !isLevelComplete(level) },
              { 'level-form-existing': isEditMode && level.id },
            ]"
          >
            <v-row align="center">
              <v-col cols="12" v-if="isEditMode" class="d-flex align-center">
                <v-chip
                  x-small
                  :color="level.id ? 'blue' : isLevelComplete(level) ? 'green' : 'warning'"
                  text-color="white"
                >
                  {{ level.id ? `Existing Level (ID: ${level.id})` : "New Level" }}
                </v-chip>
                <v-chip
                  v-if="!level.id && !isLevelComplete(level)"
                  x-small
                  color="error"
                  text-color="white"
                  class="ml-2"
                >
                  Incomplete - Won't Save
                </v-chip>
              </v-col>
            </v-row>
            <v-row align="center">
              <v-col sm="3">
                <v-text-field
                  v-model="level.min_levels"
                  outlined
                  :label="`Min Level ${index + 1}`"
                  type="number"
                  prepend-inner-icon="mdi-chevron-up"
                ></v-text-field>
              </v-col>
              <v-col sm="3">
                <v-text-field
                  v-model="level.max_levels"
                  outlined
                  :label="`Max Level ${index + 1}`"
                  type="number"
                  prepend-inner-icon="mdi-chevron-double-up"
                ></v-text-field>
              </v-col>
              <v-col sm="3">
                <v-text-field
                  v-model="level.coins"
                  outlined
                  :label="`Coins ${index + 1}`"
                  type="number"
                  prepend-inner-icon="mdi-bitcoin"
                ></v-text-field>
              </v-col>
              <v-col sm="3" class="d-flex align-center justify-center">
                <v-btn
                  v-if="levels.length > 1 || isEditMode"
                  fab
                  small
                  color="error"
                  @click="removeLevel(index)"
                >
                  <v-icon>mdi-minus</v-icon>
                </v-btn>
              </v-col>
            </v-row>
            <v-divider
              v-if="index < levels.length - 1"
              class="my-3"
            ></v-divider>
          </div>

          <!-- Add Level Button -->
          <v-row class="mt-4">
            <v-col>
              <v-btn
                outlined
                color="success"
                @click="addLevel"
                prepend-icon="mdi-plus"
              >
                <v-icon left>mdi-plus</v-icon>
                Add Another Level
              </v-btn>
            </v-col>
          </v-row>

          <v-divider class="my-5"></v-divider>

          <!-- Summary in Edit Mode -->
          <v-row v-if="isEditMode && levels.length > 0">
            <v-col>
              <v-alert
                border="left"
                colored-border
                type="info"
                elevation="2"
              >
                <v-row align="center">
                  <v-col>
                    <strong>Changes Summary:</strong>
                    <ul class="mt-2">
                      <li>
                        <v-chip x-small color="success" text-color="white">
                          New (Complete): {{ completeNewLevelsCount }}
                        </v-chip>
                      </li>
                      <li v-if="incompleteNewLevelsCount > 0">
                        <v-chip x-small color="warning" text-color="white">
                          New (Incomplete - Won't Save): {{ incompleteNewLevelsCount }}
                        </v-chip>
                      </li>
                      <li>
                        <v-chip x-small color="orange" text-color="white">
                          To Update: {{ existingLevelsCount }}
                        </v-chip>
                      </li>
                      <li>
                        <v-chip x-small color="error" text-color="white">
                          To Delete: {{ deletedLevelsCount }}
                        </v-chip>
                      </li>
                    </ul>
                  </v-col>
                </v-row>
              </v-alert>
            </v-col>
          </v-row>

          <!-- Action Buttons -->
          <v-row>
            <v-col>
              <v-btn
                rounded
                color="primary"
                :loading="isLoading"
                :disabled="isLoading || !isFormValid"
                @click="saveAllLevels"
                class="mr-3"
              >
                <v-icon left>mdi-content-save-all</v-icon>
                {{ isEditMode ? "Update" : "Save" }} All Levels ({{
                  levels.length
                }})
              </v-btn>

              <v-btn
                rounded
                outlined
                color="secondary"
                @click="resetForm"
                :disabled="isLoading"
              >
                <v-icon left>mdi-refresh</v-icon>
                Reset Form
              </v-btn>
            </v-col>
          </v-row>

          <!-- Progress Bar -->
          <v-progress-linear
            v-if="isLoading"
            :value="saveProgress"
            color="primary"
            height="6"
            class="mt-4"
          ></v-progress-linear>
        </v-card-text>
      </v-card>

      <!-- Success/Error Snackbar -->
      <v-snackbar
        v-model="snackbar.show"
        :color="snackbar.color"
        timeout="4000"
        top
      >
        {{ snackbar.message }}
        <template v-slot:action="{ attrs }">
          <v-btn text v-bind="attrs" @click="snackbar.show = false">
            Close
          </v-btn>
        </template>
      </v-snackbar>
    </v-container>
  </v-main>
</template>

<script>
import axios from "axios";

export default {
  name: "MultipleLevelCreatePage",
  data() {
    return {
      form: {
        type: "",
      },
      levels: [
        {
          type: "",
          company_id: 1,
          min_levels: "",
          max_levels: "",
          coins: "",
        },
      ],
      isLoading: false,
      saveProgress: 0,
      typeItems: [
        { id: 1, text: "Charm", value: "1" },
        { id: 2, text: "Wealth", value: "0" },
      ],
      snackbar: {
        show: false,
        message: "",
        color: "success",
      },
      isEditMode: false,
      originalLevels: [],
    };
  },
  computed: {
    hasChanges() {
      if (!this.isEditMode) return false;

      // Check if any levels were deleted
      if (this.deletedLevelsCount > 0) return true;

      // Check if any new levels were added
      if (this.newLevelsCount > 0) return true;

      // Check if any existing levels were modified
      const modified = this.levels.some((level) => {
        if (!level.id) return false; // Skip new levels
        const original = this.originalLevels.find((l) => l.id === level.id);
        if (!original) return false;

        return (
          level.min_levels != original.min_levels ||
          level.max_levels != original.max_levels ||
          level.coins != original.coins
        );
      });

      return modified;
    },
    isFormValid() {
      // Type must be selected
      if (this.form.type === "") return false;

      // In edit mode with changes, allow saving even with incomplete new levels
      // as long as there are some valid levels or deletions
      if (this.isEditMode && this.hasChanges) {
        // At least one complete level or some deletions
        const hasCompleteLevels = this.levels.some(
          (level) =>
            level.min_levels &&
            level.max_levels &&
            level.coins &&
            parseInt(level.min_levels) <= parseInt(level.max_levels)
        );

        // Allow save if there are deletions only
        if (this.deletedLevelsCount > 0 && this.levels.length === 0) {
          return true;
        }

        return hasCompleteLevels;
      }

      // Standard validation: all levels must be complete
      return this.levels.every(
        (level) =>
          level.min_levels &&
          level.max_levels &&
          level.coins &&
          parseInt(level.min_levels) <= parseInt(level.max_levels)
      );
    },
    typeLabel() {
      return this.form.type == "0" ? "Wealth" : this.form.type == "1" ? "Charm" : "";
    },
    newLevelsCount() {
      if (!this.isEditMode) return this.levels.length;
      return this.levels.filter((level) => !level.id).length;
    },
    completeNewLevelsCount() {
      if (!this.isEditMode) return 0;
      return this.levels.filter(
        (level) =>
          !level.id &&
          level.min_levels &&
          level.max_levels &&
          level.coins &&
          parseInt(level.min_levels) <= parseInt(level.max_levels)
      ).length;
    },
    incompleteNewLevelsCount() {
      if (!this.isEditMode) return 0;
      return this.levels.filter(
        (level) =>
          !level.id &&
          (!level.min_levels ||
            !level.max_levels ||
            !level.coins ||
            parseInt(level.min_levels) > parseInt(level.max_levels))
      ).length;
    },
    existingLevelsCount() {
      if (!this.isEditMode) return 0;
      return this.levels.filter((level) => level.id).length;
    },
    deletedLevelsCount() {
      if (!this.isEditMode) return 0;
      const currentIds = this.levels
        .filter((level) => level.id)
        .map((level) => level.id);
      const originalIds = this.originalLevels.map((level) => level.id);
      return originalIds.filter((id) => !currentIds.includes(id)).length;
    },
  },
  mounted() {
    const editMode = this.$route.query.edit;
    const type = this.$route.query.type;

    if (type !== undefined) {
      this.form.type = String(type);
    }

    if (editMode && type !== undefined) {
      this.isEditMode = true;
      this.loadExistingLevels(type);
    }
  },
  methods: {
    isLevelComplete(level) {
      return (
        level.min_levels &&
        level.max_levels &&
        level.coins &&
        parseInt(level.min_levels) <= parseInt(level.max_levels)
      );
    },
    async loadExistingLevels(type) {
      try {
        this.isLoading = true;
        const response = await axios.get(
          `levels?type=${type}&rowsPerPage=999`
        );
        const existingLevels = response.data.data;

        if (existingLevels.length > 0) {
          this.levels = existingLevels.map((level) => ({
            id: level.id,
            type: level.type,
            company_id: level.company_id,
            min_levels: level.min_levels,
            max_levels: level.max_levels,
            coins: level.coins,
            is_active: level.is_active,
          }));
          this.originalLevels = JSON.parse(JSON.stringify(this.levels));
        } else {
          this.levels = [
            {
              type: type,
              company_id: 1,
              min_levels: "",
              max_levels: "",
              coins: "",
            },
          ];
        }
      } catch (error) {
        console.error("Error loading existing levels:", error);
        this.showSnackbar("Error loading existing levels", "error");
      } finally {
        this.isLoading = false;
      }
    },
    addLevel() {
      this.levels.push({
        company_id: 1,
        type: this.form.type,
        min_levels: "",
        max_levels: "",
        coins: "",
      });
    },

    removeLevel(index) {
      const level = this.levels[index];

      // In edit mode, confirm deletion for existing levels
      if (this.isEditMode && level.id) {
        const confirm = window.confirm(
          `Are you sure you want to delete this level? (Min: ${level.min_levels}, Max: ${level.max_levels}, Coins: ${level.coins})`
        );
        if (!confirm) return;
      }

      // Always allow removal in edit mode, or if more than 1 level in create mode
      if (this.isEditMode || this.levels.length > 1) {
        this.levels.splice(index, 1);
      }
    },

    resetForm() {
      this.levels = [
        {
          company_id: 1,
          min_levels: "",
          max_levels: "",
          coins: "",
        },
      ];
      this.snackbar.show = false;
    },

    async saveAllLevels() {
      if (!this.isFormValid) {
        this.showSnackbar("Please fill all fields correctly", "error");
        return;
      }

      try {
        this.isLoading = true;
        this.saveProgress = 0;

        // Filter and prepare levels to save
        const levelsToSave = this.levels
          .filter((level) => {
            // In edit mode, keep:
            // 1. All existing levels (with ID) regardless of changes
            // 2. Only complete new levels (without ID)
            if (this.isEditMode) {
              if (level.id) return true; // Keep existing levels
              // For new levels, only include if complete
              return (
                level.min_levels &&
                level.max_levels &&
                level.coins &&
                parseInt(level.min_levels) <= parseInt(level.max_levels)
              );
            }
            // In create mode, keep only complete levels
            return (
              level.min_levels &&
              level.max_levels &&
              level.coins &&
              parseInt(level.min_levels) <= parseInt(level.max_levels)
            );
          })
          .map((level) => ({
            ...level,
            type: this.form.type,
          }));

        // Use unified store endpoint for both create and edit
        const response = await axios.post("/levels", {
          items: levelsToSave,
        });

        if (this.isEditMode) {
          this.showSnackbar(
            `Successfully updated levels! Created: ${response.data.created_count}, Updated: ${response.data.updated_count}, Deleted: ${response.data.deleted_count}`,
            "success"
          );
        } else {
          this.showSnackbar(
            `Successfully created ${response.data.created_count} levels!`,
            "success"
          );
        }

        this.saveProgress = 100;

        setTimeout(() => {
          this.$router.push("/levels");
        }, 2000);
      } catch (error) {
        console.error("Unexpected error:", error);
        this.showSnackbar(
          error.response?.data?.message || "An unexpected error occurred",
          "error"
        );
      } finally {
        this.isLoading = false;
      }
    },

    showSnackbar(message, color = "success") {
      this.snackbar = {
        show: true,
        message,
        color,
      };
    },

  },
};
</script>

<style scoped>
.level-form {
  background-color: #fafafa;
  padding: 16px;
  border-radius: 8px;
  margin-bottom: 16px;
  border: 2px solid transparent;
  transition: all 0.3s ease;
}

.level-form:nth-child(even) {
  background-color: #f5f5f5;
}

.level-form-new {
  border-left: 4px solid #4caf50 !important;
  background-color: #f1f8f4 !important;
}

.level-form-incomplete {
  border-left: 4px solid #ff9800 !important;
  background-color: #fff8e1 !important;
  opacity: 0.8;
}

.level-form-existing {
  border-left: 4px solid #2196f3 !important;
  background-color: #f0f7ff !important;
}

.level-form:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}
</style>
